<template>
  <div>
    <action-menu-wrapper
      data-cy="checkout-action-menu"
      :disabled="!canUpdateAndDelete"
      :items="menuItems"
      teleport
      :class="{ 'text-disabled-medium': !canUpdateAndDelete }"
      :offset="{ right: -43, top: 43 }"
    />
    <ai-agents-edit-modal
      v-if="showEditModal"
      :agent-id="model.id"
      @close="showEditModal = false"
    />
    <ai-agents-delete-modal
      v-if="showDeleteModal"
      :agent="model"
      @close="showDeleteModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import compact from "lodash/compact";

import { Ability, PermissionSubject } from "@/enums/auth";
import { usePermissions } from "@/hooks/auth";

import ActionMenuWrapper from "@/components/ui/ActionMenuWrapperV2.vue";
import AiAgentsEditModal from "@/views/AIAgents/AIAgentModal.vue";
import AiAgentsDeleteModal from "@/views/AIAgents/AIAgentsDeleteModal.vue";

import type { MenuItem } from "@/components/ui/ActionMenuWrapperV2.vue";
import type { AiAgent } from "@/models/aiAgents";

const { model } = defineProps<{
  model: AiAgent;
}>();

const { t } = useI18n();
const { canPerformActionReactive } = usePermissions();

const showDeleteModal = ref(false);
const showEditModal = ref(false);

const [canUpdate, canDelete] = [Ability.update, Ability.delete].map((action) =>
  canPerformActionReactive(PermissionSubject.aiAgent, action, {
    aiAgent: model.id,
  })
);

const canUpdateAndDelete = computed(() => canUpdate.value && canDelete.value);

const menuItems = computed<MenuItem[]>(() =>
  compact([
    canUpdate.value
      ? {
          handler: () => (showEditModal.value = true),
          label: t("COMMON.EDIT"),
        }
      : undefined,
    canDelete.value
      ? {
          handler: () => (showDeleteModal.value = true),
          label: t("COMMON.DELETE"),
        }
      : undefined,
  ])
);
</script>
